import { forwardRef, useEffect, useRef, useState } from 'react';
import './AtomicPreloader.css';

export default function AtomicPreloader() {
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    // Drive the loader off the real document load state — no artificial delay.
    const beginExit = () => setFadeOut(true);

    if (document.readyState === 'complete') {
      // App already loaded: unmount on the next frame, instantly.
      beginExit();
    } else {
      window.addEventListener('load', beginExit, { once: true });
    }

    // Safety cap: never hold the loader longer than 2s (e.g. slow networks
    // where the `load` event fires late). Only ever shortens, never delays.
    const cap = setTimeout(beginExit, 2000);

    return () => {
      window.removeEventListener('load', beginExit);
      clearTimeout(cap);
    };
  }, []);

  // Unmount once the real CSS fade transition finishes (with a safety fallback).
  useEffect(() => {
    if (!fadeOut) return;
    const el = rootRef.current;
    const remove = () => setVisible(false);
    const fallback = setTimeout(remove, 700); // matches the 0.55s CSS transition + buffer
    el?.addEventListener('transitionend', remove, { once: true });
    return () => {
      clearTimeout(fallback);
      el?.removeEventListener('transitionend', remove);
    };
  }, [fadeOut]);

  if (!visible) return null;

  return <AtomLoader ref={rootRef} className={fadeOut ? 'fade-out' : ''} />;
}

/**
 * Presentational atomic loader — reused for both the initial page preloader
 * and the lazy-route Suspense fallback so the loading visual stays consistent.
 */
export const AtomLoader = forwardRef(function AtomLoader({ className = '', ...rest }, ref) {
  return (
    <div ref={ref} className={`atomic-preloader${className ? ` ${className}` : ''}`} aria-hidden="true" {...rest}>
      {/* ── Atom ── */}
      <div className="atom-scene">
        {/* Ambient bloom */}
        <div className="atom-ambient" />

        {/* Three 3D orbital rings with electron dots */}
        <div className="atom-ring atom-ring-a"><div className="atom-dot" /></div>
        <div className="atom-ring atom-ring-b"><div className="atom-dot atom-dot-b" /></div>
        <div className="atom-ring atom-ring-c"><div className="atom-dot atom-dot-c" /></div>

        {/* Glowing nucleus with "Z" */}
        <div className="atom-nucleus">
          <span className="atom-letter">Z</span>
        </div>
      </div>

      {/* ── Text + bar ── */}
      <div className="atom-label-wrap">
        <div className="atom-name">Obaidullah Zeeshan</div>
        <div className="atom-tagline">Software Engineer</div>
        <div className="atom-progress-track">
          <div className="atom-progress-fill" />
        </div>
      </div>
    </div>
  );
});
